<script lang="ts">
  // A resolved branch history's page (docs/design.md "Branch histories"):
  // its header, then the Commits view (each commit with the pull requests
  // that brought it in and its checks, force-pushes as replaced lanes, and
  // older windows on asking), or the range timeline and the Files view,
  // which opens on the latest commit. The store (state/ref.svelte.ts) is
  // the views' host, a BranchHost: no conversation. As on a compare's page,
  // one diff model per comparison the URL names: a new one loads beside the
  // one shown, which stays until it is ready.
  import { onDestroy, untrack } from 'svelte';
  import { toast } from '../lib/state/toast.svelte';
  import { comparisonOf, timelineOf } from '../lib/logic/range';
  import { facets } from '../lib/subject';
  import { repoPath, subjectPath } from '../lib/router';
  import { RefStore } from '../lib/state/ref.svelte';
  import type { ResolvedRef } from '../lib/state/ref/fetch';
  import { DiffModel } from '../lib/state/diffs.svelte';
  import { nav } from '../lib/nav.svelte';
  import { prefs } from '../lib/state/prefs.svelte';
  import { algorithmOf } from '../lib/diff/algorithms';
  import { carryFilter } from '../lib/logic/filefilter';
  import { REF_GUIDE } from '../lib/keys/guides';
  import FindFile from '../lib/components/FindFile.svelte';
  import { findFile } from '../lib/state/findFile.svelte';
  import { carryOut, keyboard, useKeys } from '../lib/keys/keyboard.svelte';
  import { updateBranchPage, type BranchPageEffect } from '../lib/commands/page';
  import { session } from '../lib/state/session.svelte';
  import { subjectKey as relayKey } from '../lib/api/relaySubject';
  import { jumpUi, places } from '../lib/jump/jump.svelte';
  import type { RelayPage } from '../lib/components/RelayThread.svelte';
  import Rail from '../lib/components/Rail.svelte';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import PaneBoundary from '../lib/components/PaneBoundary.svelte';
  import RefHeader from '../lib/components/RefHeader.svelte';
  import RangeTimeline from '../lib/components/RangeTimeline.svelte';
  import RelayApproval from '../lib/components/RelayApproval.svelte';
  import SubjectAsk from '../lib/components/SubjectAsk.svelte';
  import Files from './Files.svelte';
  import Commits from './Commits.svelte';

  interface Props {
    resolved: ResolvedRef;
    view: 'commits' | 'files';
    /** The page's store; one is made for the resolved ref unless given (tests). */
    store?: RefStore;
  }
  let { resolved, view, store: given }: Props = $props();

  // svelte-ignore state_referenced_locally
  const store = given ?? new RefStore(resolved);
  // svelte-ignore state_referenced_locally
  if (!given) void store.open();
  onDestroy(() => {
    next?.dispose();
    model?.dispose();
    if (!given) store.dispose();
    // The toast is the app's (ToastRegion): what this page raised goes with it, as a pull request's page does.
    toast.dismiss();
  });

  // The history, resolved, is the jump bar's latest recent (App keeps a pull request's and a compare's as their pages open), and the page it shows.
  places.visit({ key: relayKey(store.subject) });
  jumpUi.here = relayKey(store.subject);
  onDestroy(() => {
    if (jumpUi.here === relayKey(store.subject)) jumpUi.here = null;
  });

  const has = $derived(facets(store.subject));
  const name = $derived(store.kind === 'commit' ? store.subject.ref.slice(0, 7) : store.subject.ref);

  $effect(() => {
    document.title = `${view === 'files' ? 'Files · ' : ''}${name}${store.subject.path === null ? '' : ' · ' + store.subject.path} · ${store.subject.owner}/${store.subject.repo}`;
  });

  const asked = $derived(comparisonOf(timelineOf(store.track, has), nav.query));
  const ws = $derived(nav.query.get('ws') === 'ignore');
  const algorithm = $derived(algorithmOf(prefs.value.diffAlgorithm));
  let model: DiffModel | null = $state(null);
  let next: DiffModel | null = $state(null);
  let modelKey = '';
  let diffRevision = $state(0);
  $effect(() => {
    // Files only: the Commits view reads no diff, so it costs no request.
    if (store.status !== 'ready' || view !== 'files' || !store.track.main.length) return;
    // The window's base moves as older commits are read: base → head is another comparison then.
    const key = `${asked.from ?? store.track.base.sha}|${asked.to}|${ws}|${algorithm}|${diffRevision}`;
    if (key === modelKey) return;
    modelKey = key;
    const m = new DiffModel(store, { from: asked.from, to: asked.to, ignoreWhitespace: ws, algorithm });
    untrack(() => {
      next?.dispose();
      if (!model) {
        model = m;
        void m.init();
        return;
      }
      next = m;
      void m.init().then(() => {
        if (m !== next) return;
        const old = model;
        model = m;
        next = null;
        old?.dispose();
      });
    });
  });

  $effect(() => {
    const anchors = store.marks.anchors;
    for (const m of [model, next] as (DiffModel | null)[]) if (m) m.setMarks(anchors);
  });

  $effect(() => {
    const m = model as DiffModel | null;
    if (view !== 'files' || !m) return;
    untrack(() => m.show());
    return () => m.hide();
  });

  /** The head moved since the diff on screen was built (a push read on focus). */
  const headMoved = $derived.by(() => {
    const m = model as DiffModel | null;
    return !!m?.head && !!store.track.head && m.head !== store.track.head;
  });

  // Each commit's pull requests and checks, read once the Commits view shows (GraphQL, so not before).
  $effect(() => {
    if (view !== 'commits' || store.status !== 'ready') return;
    void store.history;
    untrack(() => void store.loadFacts());
  });

  /**
   * Read the next older window. A range the URL names by the base would
   * mean another commit once the base moves back, so it is named by the
   * base's SHA first: the reader's range stays where it is.
   */
  async function loadOlder() {
    const base = store.track.base.sha;
    const q = { from: nav.query.get('from'), to: nav.query.get('to') };
    if (q.from === 'base' || q.to === 'base') nav.setQuery({ from: q.from === 'base' ? base : q.from, to: q.to === 'base' ? base : q.to });
    await store.loadOlder();
  }
  const older = $derived({ can: store.canLoadOlder, loading: store.loadingOlder, error: store.olderError, load: () => void loadOlder() });
  const timelineOlder = $derived({ can: store.canLoadOlder, loading: store.loadingOlder, load: loadOlder });

  /**
   * What the Commits view says besides: of the force-pushes it shows, and,
   * when the history holds merges, that the merged branches' own commits
   * are not listed (the main lane follows first parents, logic/refTrack.ts).
   */
  const notes = $derived([
    ...(store.kind !== 'branch' ? [] : [store.pushesError ?? 'Force-pushes are shown as far back as GitHub keeps this branch’s activity, with what each replaced.']),
    ...(store.history?.commits.some((c) => c.parents.length > 1 && store.track.main.some((p) => p.sha === c.sha))
      ? ['Commits merged from other branches are not listed: each merge stands for its branch (open its pull request to see them).']
      : []),
  ]);

  let mainEl: HTMLElement | undefined = $state();
  /** The agent panel about the whole history is open (the header's Ask agent). */
  let asking = $state(false);
  const headFiles = $derived(new Set((store.files ?? []).filter((f) => f.status !== 'removed').map((f) => f.filename)));

  const ask: RelayPage | null = $derived(
    session.agents
      ? {
          subject: relayKey(store.subject),
          // The branch's tip: what a question about it, or lines of it, was asked at.
          head: store.history?.tip ?? null,
          viewer: session.user?.login ?? '',
          agents: { at: 'here', where: 'in this history’s header' },
          noun: 'branch',
          draftsOff: true,
          draftsOffText: null,
        }
      : null,
  );

  useKeys({
    scope: 'pullRequest',
    guide: REF_GUIDE,
    handle: (command) => carryOut(updateBranchPage({ view, noun: 'history' }, command)[1], perform),
  });

  /** The header's ref picker is open (`, b`). */
  let picking = $state(false);

  function perform(e: BranchPageEffect) {
    switch (e.kind) {
      case 'navigate':
        return nav.go(carryFilter(subjectPath(store.subject, e.view), nav.query));
      case 'scroll-window':
        return window.scrollTo({ top: e.to === 'top' ? 0 : document.body.scrollHeight, behavior: 'instant' });
      case 'find-file':
        return findFile.fromSelection();
      case 'show-guide':
        return keyboard.showGuide();
      case 'pick':
        return void (picking = true);
    }
  }
</script>

<div class="shell">
  <Rail here={{ subject: store.subject }} />
  <main bind:this={mainEl}>
    <LoadingGate phase={store.status === 'loading' ? 'loading' : store.status === 'error' ? 'failed' : 'ready'} view={store.loadView()} onretry={() => store.load()}>
      {#if store.status === 'notfound'}
        <div class="center-msg">
          <h1>Branch not found</h1>
          <p class="muted">{store.notFound} Changeling sees exactly what your GitHub account can see.</p>
          <a class="btn" href={repoPath(store.subject.owner, store.subject.repo)}>Go to {store.subject.owner}/{store.subject.repo}</a>
        </div>
      {:else if store.history}
        {#if store.gone}
          <div class="banner" role="status">{store.gone} Showing it as it was last read.</div>
        {/if}
        {#if store.unrecorded}
          <div class="banner" role="status">{store.serverDataText}</div>
        {/if}
        <PaneBoundary what="the history's header"><RefHeader {store} {view} bind:picking onask={ask ? () => (asking = !asking) : null} /></PaneBoundary>
        {#if session.agents}<PaneBoundary what="the agent approvals"><RelayApproval subject={relayKey(store.subject)} /></PaneBoundary>{/if}
        {#if !store.track.main.length}
          <div class="center-msg">
            <h2>No commits</h2>
            <p class="muted">{store.subject.path === null ? `${name} has no commits.` : `No commit on ${name} touches ${store.subject.path}.`}</p>
          </div>
        {:else if view === 'commits'}
          <PaneBoundary what="the commits">
            <Commits
              track={store.track}
              facets={has}
              filesPath={subjectPath(store.subject, 'files')}
              linked={(sha) => store.facts.get(sha)?.pulls}
              checks={(sha) => store.facts.get(sha)?.checks}
              linkedError={store.factsError}
              {older}
              {notes}
            />
          </PaneBoundary>
        {:else if model}
          {#if headMoved}
            <div class="banner" role="status">
              New commits were pushed to {name} since this diff was loaded.
              <button class="btn small" onclick={() => diffRevision++}>Reload diff</button>
            </div>
          {/if}
          {#if store.filesMissing === null}
            <div class="banner" role="status">
              GitHub lists at most 300 files of a comparison, and this repository's trees are too large to list the rest: some changed files are not shown.
            </div>
          {/if}
          {#if store.olderError}<div class="banner" role="alert">{store.olderError}</div>{/if}
          <RangeTimeline track={store.track} facets={has} older={timelineOlder} />
          <PaneBoundary what="the files"><Files host={store} {model} updating={!!next} {ask} /></PaneBoundary>
        {/if}
      {/if}
    </LoadingGate>
  </main>
</div>

{#if asking && ask}
  <SubjectAsk page={ask} what="the history of {name}{store.subject.path === null ? '' : ' in ' + store.subject.path}" onclose={() => (asking = false)} />
{/if}

{#if store.history}
  <FindFile
    owner={store.subject.owner}
    repo={store.subject.repo}
    filesPath={subjectPath(store.subject, 'files')}
    tree={model?.repoTree ?? null}
    changed={headFiles}
    webUrl={session.config?.github_web_url ?? 'https://github.com'}
    root={mainEl ?? null}
  />
{/if}
